import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export default function LoginModal({ open, onClose }: LoginModalProps) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const inputUsername = username.trim();
    const ok = await login(inputUsername, password);
    setLoading(false);
    if (ok) {
      setUsername('');
      setPassword('');
      onClose();
      // 어드민이면 /admin, 그 외(파트너)는 /partner
      if (inputUsername === 'admin') {
        navigate('/admin');
      } else {
        navigate('/partner');
      }
    } else {
      setError('아이디 또는 비밀번호가 올바르지 않습니다. / 账号或密码错误。');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ backgroundColor: '#fff', border: '1px solid #E5DDD0' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #F0EAE0' }}>
          <div>
            <h2 className="text-[18px] font-black text-gray-900 tracking-tight">合作伙伴登录</h2>
            <p className="text-[11px] font-semibold tracking-wide mt-0.5" style={{ color: '#5C4F42' }}>거래처 로그인</p>
            <p className="text-[11px] tracking-widest mt-0.5" style={{ color: '#8C7D6E' }}>B2B合作伙伴专用 / B2B 파트너 전용</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold mb-1.5" style={{ color: '#5C4F42' }}>
              账号 <span className="font-normal text-[11px]" style={{ color: '#A09080' }}>/ 아이디</span>
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="请输入账号 / 아이디 입력"
              className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ border: '1.5px solid #D8CEBF', backgroundColor: '#FAFAF8', color: '#2A2118' }}
              onFocus={e => (e.currentTarget.style.borderColor = '#5C4F42')}
              onBlur={e => (e.currentTarget.style.borderColor = '#D8CEBF')}
              required
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold mb-1.5" style={{ color: '#5C4F42' }}>
              密码 <span className="font-normal text-[11px]" style={{ color: '#A09080' }}>/ 비밀번호</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="请输入密码 / 비밀번호 입력"
              className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ border: '1.5px solid #D8CEBF', backgroundColor: '#FAFAF8', color: '#2A2118' }}
              onFocus={e => (e.currentTarget.style.borderColor = '#5C4F42')}
              onBlur={e => (e.currentTarget.style.borderColor = '#D8CEBF')}
              required
            />
          </div>

          {error && (
            <p className="text-[12px] text-red-500 leading-snug">账号或密码错误。/ 아이디 또는 비밀번호가 올바르지 않습니다.</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl text-[14px] font-black text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: loading ? '#9A8E84' : '#2A2118' }}
          >
            {loading ? '验证中... / 확인 중...' : '登录 / 로그인'}
          </button>

          <div className="rounded-xl px-4 py-3" style={{ backgroundColor: '#F7F4F0', border: '1px solid #EDE8E0' }}>
            <p className="text-[11px] leading-relaxed font-medium" style={{ color: '#5C4F42' }}>
              <i className="ri-information-line mr-1"></i>
              如无合作账号，请通过询价联系我们。
            </p>
            <p className="text-[10px] leading-relaxed tracking-wide mt-0.5" style={{ color: '#A09080' }}>
              거래처 계정이 없으신 경우 문의하기를 통해 신청해 주세요.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
